import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import {
  DocumentFileTemplateKeyFormDto,
  DocumentFileTemplateKeyFormInterface
} from '@zaoblako/shared/modules/catalog/dtos/document-file-template-key/document-file-template-key-form.dto';
import { DocumentFileTemplateKeyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template-key/document-file-template-key-find.dto';
import type { DocumentFileTemplateKeyBriefFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template-key/document-file-template-key-brief-find.dto';
import { Observable } from 'rxjs';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { BasisDocumentFileTemplateKeyService } from '@zaoblako/web/core/services/basis/basis-document-file-template-key.service';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { DocumentFileTemplateKeyTargetSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/document-file-template-key-target-single-select/document-file-template-key-target-single-select.component';
import { DocumentFileTemplateKeyKindEnum } from '@zaoblako/shared/modules/catalog/enums/document-file-template-key-kind.enum';
import { DocumentFileTemplateKeyKindEnumI18n } from '@zaoblako/shared/modules/catalog/i18n/document-file-template-key-kind-enum.i18n';
import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { DocumentFileTemplateKeyTargetChildrenFindInterface } from '@zaoblako/shared/modules/catalog/dtos/document-file-template-key/document-file-template-key-target-children-find.dto';

@Component({
  selector: 'crm-document-file-template-key-form',
  standalone: true,
  imports: [
    AsyncPipe,
    ButtonComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    SpinnerComponent,
    DocumentFileTemplateKeyTargetSingleSelectComponent,
    SingleSelectComponent
  ],
  templateUrl: './document-file-template-key-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DocumentFileTemplateKeyFormComponent
  extends BaseFormComponent<DocumentFileTemplateKeyFormInterface>
  implements OnInit {
  @Input() public readonly index: number;
  public key: DocumentFileTemplateKeyFindInterface = <DocumentFileTemplateKeyFindInterface>{
    kind: DocumentFileTemplateKeyKindEnum.ORDINARY
  };

  public dto = DocumentFileTemplateKeyFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public target: DocumentFileTemplateKeyTargetChildrenFindInterface;

  public readonly kindEnum = DocumentFileTemplateKeyKindEnum;

  public readonly kindOptions: { id: DocumentFileTemplateKeyKindEnum; name: string }[] = [
    {
      id: DocumentFileTemplateKeyKindEnum.ORDINARY,
      name: DocumentFileTemplateKeyKindEnumI18n[I18nLocalesEnum.RU_RU][DocumentFileTemplateKeyKindEnum.ORDINARY]
    },
    {
      id: DocumentFileTemplateKeyKindEnum.AMOUNT_IN_WORDS,
      name: DocumentFileTemplateKeyKindEnumI18n[I18nLocalesEnum.RU_RU][DocumentFileTemplateKeyKindEnum.AMOUNT_IN_WORDS]
    }
  ];

  private numericSourceKeyCandidates: DocumentFileTemplateKeyBriefFindInterface[] = [];

  public constructor(
    private readonly basisDocumentFileTemplateKeyService: BasisDocumentFileTemplateKeyService,
    public readonly formStoreService: FormStoreService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService
  ) {
    super();
  }

  public ngOnInit() {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.refreshNumericSourceKeyCandidates();

    if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.key = {
        ...this.key,
        kind: DocumentFileTemplateKeyKindEnum.ORDINARY
      };
    }

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public get sourceKeyOptions(): DocumentFileTemplateKeyBriefFindInterface[] {
    return this.numericSourceKeyCandidates.filter((candidate) => candidate.id !== this.key?.id);
  }

  public get selectedKindOption(): { id: DocumentFileTemplateKeyKindEnum; name: string } {
    const resolvedKind = this.key.kind ?? DocumentFileTemplateKeyKindEnum.ORDINARY;
    return this.kindOptions.find((option) => option.id === resolvedKind) ?? this.kindOptions[0];
  }

  public onKindOptionChange(option: { id: DocumentFileTemplateKeyKindEnum; name: string } | null) {
    if (!option) {
      return;
    }
    this.key.kind = option.id;
    if (option.id === DocumentFileTemplateKeyKindEnum.ORDINARY) {
      this.key.sourceKey = undefined;
    }
    if (option.id === DocumentFileTemplateKeyKindEnum.AMOUNT_IN_WORDS) {
      this.target = undefined;
      this.key.target = '';
      this.key.path = '';
      this.refreshNumericSourceKeyCandidates();
    }
    this.cdRef.markForCheck();
  }

  public get selectedSourceKey(): DocumentFileTemplateKeyBriefFindInterface | null {
    const sourceKeyId = this.key.sourceKey?.id;
    if (sourceKeyId === undefined || sourceKeyId === null) {
      return null;
    }
    const matchedCandidate = this.sourceKeyOptions.find((candidate) => candidate.id === sourceKeyId)
      ?? this.numericSourceKeyCandidates.find((candidate) => candidate.id === sourceKeyId);
    if (matchedCandidate) {
      return matchedCandidate;
    }
    if (this.key.sourceKey && this.key.sourceKey.id === sourceKeyId) {
      const nestedSourceKey = this.key.sourceKey;
      return {
        id: nestedSourceKey.id,
        name: nestedSourceKey.name,
        kind: nestedSourceKey.kind ?? DocumentFileTemplateKeyKindEnum.ORDINARY,
        target: nestedSourceKey.target,
        path: nestedSourceKey.path
      };
    }
    return null;
  }

  public onSourceKeyChange(selectedSourceKey: DocumentFileTemplateKeyFindInterface | null) {
    this.key.sourceKey = selectedSourceKey
      ? {
        id: selectedSourceKey.id,
        name: selectedSourceKey.name,
        kind: selectedSourceKey.kind ?? DocumentFileTemplateKeyKindEnum.ORDINARY,
        target: selectedSourceKey.target,
        path: selectedSourceKey.path
      }
      : undefined;
    this.cdRef.markForCheck();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.basisDocumentFileTemplateKeyService
      .findById<DocumentFileTemplateKeyFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (loadedKey) => {
          this.key = {
            ...loadedKey,
            kind: loadedKey.kind ?? DocumentFileTemplateKeyKindEnum.ORDINARY
          };
          this.refreshNumericSourceKeyCandidates();
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveKey() {
    const kind = this.key.kind ?? DocumentFileTemplateKeyKindEnum.ORDINARY;
    const payload: DocumentFileTemplateKeyFormInterface = {
      name: this.key.name,
      kind,
      target: this.key.target ?? '',
      path: this.key.path ?? '',
      sourceKey: kind === DocumentFileTemplateKeyKindEnum.AMOUNT_IN_WORDS ? this.key.sourceKey : undefined
    };

    if (this.checkValidate(payload).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.basisDocumentFileTemplateKeyService
          .update<DocumentFileTemplateKeyFormInterface & { id: number }, DocumentFileTemplateKeyFindInterface>({
            ...payload,
            id: this.key.id
          })
          .subscribe({
            next: (savedKey) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Ключ "${this.key.name}" обновлен`);
              this.formStoreService.saveNext(savedKey, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.basisDocumentFileTemplateKeyService.create<DocumentFileTemplateKeyFindInterface>(payload).subscribe({
          next: (savedKey) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Ключ создан');
            this.formStoreService.saveNext(savedKey, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteKey() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить ключ "${this.key.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisDocumentFileTemplateKeyService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Ключ "${this.key.name}" удален`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public restoreKey() {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить ключ "${this.key.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.basisDocumentFileTemplateKeyService
          .restore(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.key = { ...this.key, deleted: null };
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Ключ "${this.key.name}" восстановлен`);
              this.formStoreService.saveNext(this.key, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public changeTarget(target: DocumentFileTemplateKeyTargetChildrenFindInterface) {
    if (target) {
      this.key.target = target.target;
      this.key.path = target.path;
    } else {
      this.key.target = null;
      this.key.path = null;
    }
  }

  private refreshNumericSourceKeyCandidates(): void {
    const entityId = this.formStoreService.getState.forms[this.index]?.entityId;
    const excludeId = entityId ?? this.key?.id;
    this.basisDocumentFileTemplateKeyService.getNumericSourceKeyCandidates(excludeId).subscribe({
      next: (items) => {
        this.numericSourceKeyCandidates = items;
        this.cdRef.markForCheck();
      }
    });
  }
}
